import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { fromEvent, interval, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

export interface PlayerSource {
  src: string;
  type: string;
}

export interface PlayerLegend {
  src: string;
  srclang: string;
  label: string;
}

@Component({
  selector: 'app-amp-player',
  templateUrl: './amp-player.component.html',
  styleUrls: ['./amp-player.component.scss'],
})
export class AmpPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('video', { static: true })
  videoPlayer: ElementRef<HTMLVideoElement>;

  @Input() id: string;
  @Input() source: PlayerSource;
  @Input() autoplay = false;
  @Input() legends: PlayerLegend[] = [];
  @Input() currentTime = 0;

  @Output() ended = new EventEmitter<boolean>(false);
  @Output() seeking = new EventEmitter<number>();
  @Output() currentTiming = new EventEmitter<number>();

  private destroyInterval$ = new Subject<boolean>();

  private subscription: Subscription[] = [];
  private player: any;

  ngOnInit() {
    if (!this.source.src && this.source.type) {
      throw new Error('Source must be specified');
    }

    this.initPlayer();
  }

  ngOnDestroy(): void {
    this.destroyInterval$.next(true);
    this.destroyInterval$.complete();

    this.subscription.forEach(subscription => subscription.unsubscribe());
  }

  initPlayer() {
    this.player = window.amp(this.videoPlayer.nativeElement, {
      /* Options */
      nativeControlsForTouch: false,
      autoplay: this.autoplay,
      controls: true,
      id: this.id,
      logo: { enabled: false },
    });

    const sources = [this.source];
    const legends = this.legends
      ? this.legends.map(legend => ({
          ...legend,
          kind: 'Legenda',
        }))
      : this.legends;

    this.player.src(sources, legends);

    this.listenerEventEffects();
  }

  private listenerEventEffects(): void {
    this.subscription.push(
      fromEvent(this.player, window.amp.eventName.loadeddata).subscribe(() =>
        this.player.currentTime(this.currentTime)
      ),
      fromEvent(this.player, window.amp.eventName.ended).subscribe(() =>
        this.ampEnded()
      ),
      fromEvent(this.player, window.amp.eventName.pause).subscribe(() =>
        this.destroyInterval$.next(true)
      ),
      fromEvent(this.player, window.amp.eventName.playing)
        .pipe(
          tap(() => this.destroyInterval$.next(false)),
          switchMap(() =>
            interval(10 * 1000).pipe(takeUntil(this.destroyInterval$))
          )
        )
        .subscribe(() => this.ampCurrentTime())
    );
  }

  private ampCurrentTime() {
    const currentTime = this.player.currentTime();
    this.currentTiming.emit(Number(currentTime?.toFixed(2) || 0));
  }

  private ampEnded() {
    this.ended.emit(true);
  }
}

